/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueErrorSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';
import { prepareAnalyticsDateRange } from 'util/appUtils';
import { defaultTimeframeList } from 'components/kea/DateRangePicker/timeframeList';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Maps bridge reportType strings to slice state keys.
 * Used in pending/fulfilled/rejected handlers to target the right report.
 */
const REPORT_KEYS = {
  top_queries:                   'topQueries',
  zero_results:                  'zeroResults',
  lost_opportunities:            'lostOpportunities',
  products_popular_from_search:  'popularProducts',
};

/**
 * Returns a fresh empty report sub-state.
 * Called for initialState and clearReport.
 */
function emptyReport() {
  return {
    isFetched:  false,
    isFetching: false,
    error:      null,
    data:       [],
    users:      null,
    queries:    null,
    curve:      { data: [] },
  };
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const loadAnalyticsReport = createAsyncThunk(
  'analyticsSearch/loadReportAsync',
  async ({ shopId, reportType }, { extra, getState, dispatch, rejectWithValue }) => {
    try {
      const { analyticsApi } = extra.serviceRegistry;
      if (!analyticsApi) {
        return rejectWithValue('analyticsApi not available on current transport');
      }

      const timeframe = getState().analyticsSearch.timeframe;
      const { from, to } = prepareAnalyticsDateRange(timeframe);

      const result = await analyticsApi.loadReport(shopId, reportType, { limit: 50, from, to });
      return { reportType, ...result };
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('notifications.error.fetchingError', { entity: 'analytics' }),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'load failed');
    }
  },
);

export const loadAnalyticsQueryDetail = createAsyncThunk(
  'analyticsSearch/loadQueryDetailAsync',
  async ({ shopId, query }, { extra, getState, dispatch, rejectWithValue }) => {
    try {
      const { analyticsApi } = extra.serviceRegistry;
      if (!analyticsApi?.loadQueryDetail) {
        return rejectWithValue('Query analytics are not available on the current transport');
      }
      const timeframe = getState().analyticsSearch.timeframe;
      const { from, to } = prepareAnalyticsDateRange(timeframe);
      return await analyticsApi.loadQueryDetail(shopId, query, { from, to });
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('notifications.error.fetchingError', { entity: 'query analytics' }),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'load failed');
    }
  },
);

export const exportAnalyticsReport = createAsyncThunk(
  'analyticsSearch/exportReportAsync',
  async ({ shopId, reportType }, { extra, getState, dispatch, rejectWithValue }) => {
    try {
      const { analyticsApi } = extra.serviceRegistry;
      if (!analyticsApi?.exportReport) {
        return rejectWithValue('Analytics export is not available on the current transport');
      }
      const timeframe = getState().analyticsSearch.timeframe;
      const { from, to } = prepareAnalyticsDateRange(timeframe);

      return await analyticsApi.exportReport(shopId, reportType, { from, to });
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('analytics.export.failed'),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'export failed');
    }
  },
);

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState = {
  topQueries:        emptyReport(),
  zeroResults:       emptyReport(),
  lostOpportunities: emptyReport(),
  popularProducts:   { isFetching: false, data: [], error: null },
  queryDetail: {
    isFetched: false,
    isFetching: false,
    error: null,
    query: null,
    metrics: { searches: 0, clicks: 0, noActions: 0, ctr: 0 },
    curve: [],
    relatedQueries: [],
    relatedProducts: [],
    relatedQueriesUnavailable: false,
    relatedProductsUnavailable: false,
    relatedQueriesSampled: false,
    relatedQueriesSampleSize: 0,
  },
  timeframe:         defaultTimeframeList.last30days,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const slice = createSlice({
  name: 'analyticsSearch',
  initialState,
  reducers: {
    setTimeframe: (state, { payload }) => {
      state.timeframe = payload;
    },
    clearReport: (state, { payload }) => {
      const key = REPORT_KEYS[payload];
      if (key) {
        state[key] = emptyReport();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAnalyticsReport.pending, (state, { meta }) => {
        const key = REPORT_KEYS[meta.arg.reportType];
        if (key) {
          state[key].isFetching = true;
          state[key].error = null;
        }
      })
      .addCase(loadAnalyticsReport.fulfilled, (state, { payload }) => {
        const key = REPORT_KEYS[payload.reportType];
        if (key) {
          state[key].isFetching = false;
          state[key].isFetched  = true;
          state[key].data       = payload.data;
          state[key].users      = payload.users;
          state[key].queries    = payload.queries;
          state[key].curve      = payload.curve;
        }
      })
      .addCase(loadAnalyticsReport.rejected, (state, { payload, meta }) => {
        const key = REPORT_KEYS[meta.arg.reportType];
        if (key) {
          state[key].isFetching = false;
          state[key].error      = payload || 'load failed';
        }
      })
      .addCase(loadAnalyticsQueryDetail.pending, (state) => {
        state.queryDetail.isFetching = true;
        state.queryDetail.error = null;
      })
      .addCase(loadAnalyticsQueryDetail.fulfilled, (state, { payload }) => {
        state.queryDetail = {
          isFetched: true,
          isFetching: false,
          error: null,
          query: payload.query,
          metrics: payload.metrics,
          curve: payload.curve,
          relatedQueries: payload.relatedQueries || [],
          relatedProducts: payload.relatedProducts || [],
          relatedQueriesUnavailable: payload.relatedQueriesUnavailable === true,
          relatedProductsUnavailable: payload.relatedProductsUnavailable === true,
          relatedQueriesSampled: payload.relatedQueriesSampled === true,
          relatedQueriesSampleSize: Number(payload.relatedQueriesSampleSize || 0),
        };
      })
      .addCase(loadAnalyticsQueryDetail.rejected, (state, { payload }) => {
        state.queryDetail.isFetching = false;
        state.queryDetail.error = payload || 'load failed';
      });
  },
});

export const { actions } = slice;
export default slice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectTopQueries        = (state) => state.analyticsSearch.topQueries;
export const selectZeroResults       = (state) => state.analyticsSearch.zeroResults;
export const selectLostOpportunities = (state) => state.analyticsSearch.lostOpportunities;
export const selectPopularProducts   = (state) => state.analyticsSearch.popularProducts;
export const selectTimeframe         = (state) => state.analyticsSearch.timeframe;
export const selectQueryDetail       = (state) => state.analyticsSearch.queryDetail;

/**
 * Curried selector: selectReportIsLoading('top_queries')(state)
 */
export const selectReportIsLoading = (reportType) => (state) => {
  const key = REPORT_KEYS[reportType];
  return key ? state.analyticsSearch[key].isFetching : false;
};
